/* eslint-disable react/no-danger */
/* product description is html */
import React, { FunctionComponent, useState, useEffect } from 'react';
import styled from 'styled-components';
import { parse, stringify } from 'query-string';
import { useMedia } from 'react-media-match';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import Headline from '@empiriecom/module-components/Headline';
import Price from '@empiriecom/module-components/Price';
import WishlistProvider from '@empiriecom/module-components/WishListProvider';
import { browserSupportsGrid } from '@empiriecom/module-components/Grid';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import DynamicYield from '@empiriecom/module-components/DynamicYield';
import { ProductAttributes } from '@/src/components/ProductAttributes';
import ProductRecoSlider from '@empiriecom/module-components/ProductRecoSlider';
import { FragmentConfiguration } from '@/config';
import { Picker } from '@/src/components/Picker';
import { GalleryContextProvider } from '@/src/components/ImageGallery/GalleryContext';
import CustomerRatings from '@/src/components/CustomerRatings';
import { ArticleServices } from '@/src/components/ArticleServices';
import SizeHint from '@/src/components/SizeHint';
import { AttributeValueObject, MediaAsset } from '@/src/interfaces';
import { useLocation, useHistory, useParams, Redirect } from 'react-router-dom';
import useDebounce from '@empiriecom/module-components/hooks/useDebounce';
import { History } from 'history';
import ProductSelection from '../ProductSelection';
import Card, { Section } from '../Card';
import { ImageGallery } from '../ImageGallery';
import { AddToWishlist } from '../AddToWishlist';
import { AddToBasket, AddToBasketModal } from '../AddToBasket';
import { InstallmentCalculator } from '../InstallmentCalculator';
import { ProductDescription } from '../ProductDescription';
import { Tracking } from '../Tracking';
import { ReviewStars } from '../ReviewStars';
import { DetailViewSkeleton } from '../Skeleton';
import { Navigation } from '../Navigation';
import { TaxHint } from '../TaxHint';
import { VoucherPrice } from '../VoucherPrice';
import { AvailabilityMessage } from '../Delivery/AvailabilityMessage';
import { DeliveryCard } from '../Delivery/DeliveryCard';
import { ProductHeadline } from '../ProductHeadline';
import { StickyArea, VisibilityWrapper, StickyContext } from '../StickyArea';
import ErrorRedirect from '../App/ErrorRedirect';
import SeoElement from '../SeoElement';
import PremiumService from '../PremiumService';
import SampleOrder from '../SampleOrder';
import QuantitySelection from '../QuantitySelection';
import PaybackLabel from '../PaybackLabel';
import SeoImageLink from '../SeoImageLink';
import useHashParameters from '../hooks/useHashParameters';
import { useCategory, useProduct, useSeo } from '../ProductContext';
import usePage from '../ProductContext/hooks/usePage';
import SchemaOrg from '../Tracking/SchemaOrg';
import CollapsibleContextProvider from '../Card/CollapsibleContext';
import setVarationFunction from './setVariation';
import { CliplisterImageGallery, Grover, LoadBee } from '../ExternalServices';
import InstallmentCalculatorVariant from '../InstallmentCalculator/InstallmentCalculatorVariant';
import InstallmentCalculatorContextProvider from '../InstallmentCalculator/InstallmentCalculatorContext';
import PowerEfficiency from '../PowerEfficiency';
// import useGoogleOptimize from '../hooks/useGoogleOptimize';

const fallbackRDEHeadlines = defineMessages({
  alternativeProductsFallback: {
    defaultMessage: 'Ähnliche Artikel',
    id: 'rde.alternative-products.headline-fallback',
  },
  criteoFallback: {
    defaultMessage: 'Gesponserte Artikel',
    id: 'rde.criteo-products.headline-fallback',
  },
  prudSysFallback: {
    defaultMessage: 'Andere Kunden interessierte auch',
    id: 'rde.prudsys.headline-fallback',
  },
  seriesFallback: {
    defaultMessage: 'Mehr aus dieser Serie',
    id: 'rde.series.headline-fallback',
  },
});

export const PageWrapper = styled.div`
  margin: 0 auto;
  max-width: 1140px;
`;

const ProductHeadlineWrapper = styled.div<{ mobile: boolean }>`
  ${({ mobile }) => mobile && 'margin: 0.75rem 0.75rem 0'};
  h1 {
    width: auto;
    margin-bottom: 0.3rem;
    font-size: 1.125rem;
    font-weight: 400;
    line-height: normal;
    color: ${({ theme }) => theme.base.color.text};
  }
`;

const PageGrid = styled.div<{ isMobile: boolean; isGridSupported: boolean }>`
  ${({ isGridSupported }) => (isGridSupported ? 'display: grid;' : 'display: flex;')};
  ${({ isMobile }) =>
    isMobile
      ? 'grid-template-columns: repeat(1, 100%);'
      : 'grid-template-columns: repeat(2, 63% 37%);'};
  ${({ isMobile }) => (isMobile ? 'flex-flow: row wrap;' : 'flex-flow: row nowrap;')}
  width: 100%;
  ${({ isMobile }) => isMobile && 'overflow: hidden;'}
`;

const ImageGalleryBox = styled.div<{ mobile: boolean; isGridSupported: boolean }>`
  ${({ mobile, isGridSupported }) => !isGridSupported && (mobile ? 'width: 100%;' : 'width: 63%;')};
  ${({ mobile }) => mobile && 'position: relative;'};
`;

const BuyBox = styled.div<{ mobile: boolean; isGridSupported: boolean }>`
  ${({ mobile, isGridSupported }) => !isGridSupported && (mobile ? 'width: 100%;' : 'width: 37%;')};
`;

/* use VisibilityWrapper to toggle sticky area */
const PickerWrapper = styled(VisibilityWrapper)`
  position: absolute;
  bottom: 6.75rem;
  right: 0.75rem;
  z-index: 10;
`;

const PriceAndEEK = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  flex-flow: ${({ mobile }) => (mobile ? 'row wrap;' : 'row nowrap;')};
  align-items: ${({ mobile }) => (mobile ? 'center;' : 'flex-end;')};
  margin: ${({ mobile }) => (mobile ? '1rem 0.75rem 0;' : '1rem auto 0;')};
  position: relative;
`;

const PriceWrapper = styled.div``;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  flex-flow: row wrap;
  @media only screen and (max-width: 1024px) {
    margin-right: 1rem;
  }
`;

const ProductDetails = styled.div<{ twoColumn: boolean; mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: ${({ mobile }) => (mobile ? 'row wrap;' : 'row nowrap;')};
  #product-description-column {
    flex: 1;
    ${({ twoColumn }) => twoColumn && 'width: 60%;'};
    ${({ twoColumn, mobile }) => twoColumn && mobile && 'width: 100%;'};
  }
  #product-attributes-column {
    ${({ twoColumn }) => twoColumn && 'width: 40%;'};
    ${({ twoColumn, mobile }) => twoColumn && mobile && 'width: 100%;'};
  }
`;

/* use VisibilityWrapper to toggle sticky area */
const BasketButtonWrapper = styled(VisibilityWrapper)`
  width: 85%;
  padding-right: 0.5rem;
`;

const WishlistButtonWrapper = styled.div`
  width: 15%;
`;

const ScrollTarget = styled.div`
  height: 0;
  line-height: 0;
  overflow: hidden;
`;

const PageContainer: FunctionComponent = (): JSX.Element => {
  const [isGridSupported, setIsGridSupported] = useState<boolean>(true);
  const [highlightUnselectedDimension, setHighlightUnselectedDimension] = useState<boolean>(false);
  const [isStickyVisible, setIsStickyVisible] = useState<boolean>(false);
  const config = useConfig<FragmentConfiguration>();
  const { hash, search, pathname } = useLocation();
  const history = useHistory();
  const intl = useIntl();
  const { isBot } = usePage();
  const { productId, seoId } = useParams<{ productId: string; seoId: string }>();

  useEffect(() => {
    if (!browserSupportsGrid()) {
      setIsGridSupported(false);
    }
  }, []);

  const { position, variationId } = useHashParameters();

  const { currentProductId, error, isLoading, product, setIsFallback } = useProduct();
  const { error: seoError, isLoading: seoLoading, seoData } = useSeo();
  const { category } = useCategory();
  const isDebounceLoading = useDebounce(isLoading, 300);

  // const optimizeTestValue = useGoogleOptimize(config.page.values.googleOptimizeExperimentId);

  // const Orientation = createMediaMatcher({
  //   portrait: '(orientation: portrait)',
  //   landscape: '(orientation: landscape)',
  // });
  const isMobile = useMedia({
    desktop: false,
    mobile: true,
    tablet: false,
  });

  // Overwrite setVariation function to reset fallback
  const setVariation = (varId: string, historyParam?: History, isBotParam: boolean = false) => {
    setIsFallback(false);
    return setVarationFunction(varId, historyParam, isBotParam);
  };

  /* return redirect for no product ids */
  if (!currentProductId || error) {
    if (error && variationId !== null && currentProductId === variationId) {
      // eslint-disable-next-line no-console
      console.info(`Made fallback to AKLBB for SKU ${variationId}`);

      let fallbackHash: string;
      let fallbackSearch: string;
      const parsedHash = parse(hash);
      const parsedSearch = parse(search);
      if (product) {
        fallbackHash = stringify({
          ...parsedHash,
          sku: product.sku,
        });
        fallbackSearch = stringify({
          ...parsedSearch,
          sku: product.sku,
        });
      } else {
        delete parsedHash.sku;
        fallbackHash = stringify(parsedHash);
        delete parsedSearch.sku;
        fallbackSearch = stringify(parsedSearch);
      }

      const fallbackUrl = `${pathname}${fallbackSearch !== '' ? `?${fallbackSearch}` : ''}${
        fallbackHash !== '' ? `#${fallbackHash}` : ''
      }`;
      setIsFallback(false);
      return <Redirect to={fallbackUrl} />;
    }
    if (!isDebounceLoading && !!error) {
      setIsFallback(false);
      return <ErrorRedirect />;
    }
  }

  /* render Skeleton if product is not loaded yet */
  if (isDebounceLoading || !product) {
    return (
      <PageWrapper data-testid={`page-container-${productId}`}>
        <DetailViewSkeleton />
      </PageWrapper>
    );
  }

  const {
    akl,
    attributes = {},
    availability,
    defaultCategory,
    deliveryType,
    dimensions,
    ean,
    images,
    media,
    name,
    orderNumber,
    price,
    promotion,
    sku,
    slug,
    quantitySelection,
    hasServices,
  } = product;

  // TODO: remove when switch to media property is complete
  /** legacy support if media prop is not available */
  const legacyImages: MediaAsset[] = [];
  images.map((image) => legacyImages.push({ type: 'IMAGE', url: image }));

  /** add locale prefix to URL according to config */
  const [currentLocale] = config.page.values.locales.filter((obj) => obj.locale === intl.locale);
  const { prefix } = currentLocale;
  if (prefix === '' && pathname.indexOf('/p/') !== 0) {
    return (
      <Redirect
        to={`${pathname.slice(pathname.indexOf('/p/'), pathname.length)}${search}${hash}`}
      />
    );
  }
  if (prefix !== '' && pathname.indexOf(prefix) !== 0) {
    return <Redirect to={`${prefix}${pathname}${search}${hash}`} />;
  }

  /** replace URL if it has no seoId or there is no valid variationId */
  if (
    typeof window !== 'undefined' &&
    ((!seoId && typeof slug !== 'undefined' && slug !== '') ||
      !variationId ||
      (isBot && search.indexOf('sku=') > -1))
  ) {
    const updatedHash = stringify({
      ...parse(hash),
      sku: productId === akl ? sku : productId,
    });
    const updatedSearchObject = {
      ...parse(search),
      sku: productId === akl ? sku : productId,
    };
    if (isBot) {
      delete updatedSearchObject.sku;
      delete updatedSearchObject['nav-c'];
    }
    const updatedSearch = stringify(updatedSearchObject);
    const sanitizedUrl = `${prefix}/p/${
      typeof slug !== 'undefined' && slug !== '' ? `${slug}/` : ''
    }${akl}${updatedSearch ? `?${updatedSearch}` : ''}#${updatedHash}`;
    return <Redirect to={sanitizedUrl} />;
  }

  const page = (
    <>
      <Tracking product={product || undefined} position={parseInt(position, 10)} />
      <SchemaOrg />
      <PageWrapper data-testid={`page-container-${productId}`}>
        <>
          {config.dynamicYieldEnabled && (
            <>
              <DynamicYield dataDyId="dv_notification-campaign" />
              <DynamicYield dataDyId="dv-mkz-banner-popup" />
            </>
          )}
          <div data-cypress-id="page-navigation">
            <Navigation isMobile={isMobile} />
          </div>
          <WishlistProvider>
            <StickyContext.Provider
              value={{
                isVisible: isStickyVisible,
                setIsVisible: (isVisible) => setIsStickyVisible(!isVisible),
              }}
            >
              <CollapsibleContextProvider>
                <InstallmentCalculatorContextProvider>
                  <StickyArea
                    product={product}
                    isContentVisible={!isStickyVisible}
                    visibilityOffset={500}
                    setHighlightUnselectedDimension={setHighlightUnselectedDimension}
                  />
                  <PageGrid
                    isMobile={isMobile}
                    isGridSupported={isGridSupported}
                    data-cypress-id="page-grid"
                  >
                    <ImageGalleryBox mobile={isMobile} isGridSupported={isGridSupported}>
                      <GalleryContextProvider>
                        {config.externalServices.cliplister.enabled ? (
                          <CliplisterImageGallery />
                        ) : (
                          <ImageGallery images={media || legacyImages} name={name} sku={sku} />
                        )}
                        {config.dynamicYieldEnabled && (
                          <>
                            <DynamicYield dataDyId="dv_social-proof-inject" />
                            <DynamicYield dataDyId="dv_social-proof" />
                          </>
                        )}
                      </GalleryContextProvider>
                      {typeof window !== 'undefined' &&
                        isMobile &&
                        dimensions.filter((dimension) => dimension.axisType === 'COLOR').length ===
                          1 && (
                          <PickerWrapper>
                            <Picker
                              dimension={
                                dimensions.filter((dimension) => dimension.axisType === 'COLOR')[0]
                              }
                              setVariation={setVariation}
                              history={history}
                            />
                          </PickerWrapper>
                        )}
                    </ImageGalleryBox>
                    <BuyBox
                      mobile={isMobile}
                      isGridSupported={isGridSupported}
                      data-cypress-id="buy-box"
                    >
                      <ProductHeadlineWrapper data-cypress-id="buybox-headline" mobile={isMobile}>
                        <Headline level={1}>{name}</Headline>
                        <ReviewStars />
                      </ProductHeadlineWrapper>
                      <PriceAndEEK mobile={isMobile}>
                        <PriceWrapper data-testid="product-price">
                          <Price
                            amount={price.amount && price.amount}
                            currency={price.currency}
                            isMSRP={price.uvp && price.uvp}
                            isStartPrice={price.isStartPrice && price.isStartPrice}
                            layout={config.price.layout}
                            oldPrice={price.valueOld && price.valueOld}
                            price={price.value}
                            saving={price.saving && price.saving.value}
                            savingPrefix
                            savingType={
                              price.saving && price.saving.type ? price.saving.type : 'CURRENCY'
                            }
                            unit={price.unit && price.unit}
                            unitPrice={price.valueUnit && price.valueUnit}
                          />
                          <TaxHint deliveryType={deliveryType} />
                        </PriceWrapper>
                      </PriceAndEEK>
                      <VoucherPrice price={price} />
                      {attributes &&
                        attributes?.powerefficiency &&
                        attributes?.powerefficiency?.values?.filter(
                          (val) => val?.type === 'powerEfficiency',
                        ).length > 0 && <PowerEfficiency attribute={attributes?.powerefficiency} />}
                      {config.dynamicYieldEnabled && <DynamicYield dataDyId="dv_flag" />}
                      {attributes.services &&
                        attributes.services.values.map((service) =>
                          service.values.map((value: AttributeValueObject) => (
                            <PremiumService value={value} />
                          )),
                        )}
                      <InstallmentCalculator source="pdv" />
                      {attributes.features &&
                        attributes.features.values.map(
                          ({ type, values }, index) =>
                            type === 'payback' && (
                              <PaybackLabel
                                key={'payback'.concat(index.toFixed())}
                                points={values.join('')}
                              />
                            ),
                        )}
                      {config.externalServices.grover.enabled && (
                        <Grover availabilityState={availability?.state} productId={orderNumber} />
                      )}
                      {quantitySelection && <QuantitySelection />}
                      <ProductSelection
                        product={product}
                        setProduct={(prodId: string) => setVariation(prodId, history, isBot)}
                        highlightUnselectedDimension={highlightUnselectedDimension}
                        setHighlightUnselectedDimension={(isHighlight) =>
                          setHighlightUnselectedDimension(isHighlight)
                        }
                      />
                      {attributes.features &&
                        attributes.features.values.map(
                          ({ type, values }, index) =>
                            type === 'sizeHint' && (
                              <SizeHint key={'sizeHint'.concat(index.toFixed())} values={values} />
                            ),
                        )}
                      {availability ? (
                        <DeliveryCard
                          availability={availability}
                          deliveryAttribute={attributes.delivery}
                        />
                      ) : (
                        <AvailabilityMessage
                          state={availability.state}
                          message={availability.message}
                        />
                      )}
                      {config.dynamicYieldEnabled && <DynamicYield dataDyId="dv_mkz-banner" />}
                      {config.articleServices.enabled && hasServices && (
                        <ArticleServices
                          orderNumber={orderNumber}
                          price={price.value}
                          premiumServices={undefined}
                        />
                      )}
                      {attributes.features &&
                        attributes.features.values.map(
                          ({ type, values }, index) =>
                            type === 'sampleOrder' && (
                              <SampleOrder
                                key={'sampleOrder'.concat(index.toFixed())}
                                values={values}
                              />
                            ),
                        )}
                      {!isMobile && (
                        <ButtonWrapper data-cypress-id="add-to-basket-desktop">
                          <BasketButtonWrapper>
                            <AddToBasket
                              setHighlightUnselectedDimension={setHighlightUnselectedDimension}
                              isSelected={availability && availability.state !== 'UNSELECTED'}
                            />
                          </BasketButtonWrapper>
                          <WishlistButtonWrapper>
                            <AddToWishlist sku={sku} showLabel={false} />
                          </WishlistButtonWrapper>
                        </ButtonWrapper>
                      )}
                      <AddToBasketModal />
                    </BuyBox>
                  </PageGrid>
                  <div id="mobile-card-scroll-target" />
                  <Section
                    headline={
                      <FormattedMessage
                        id="page.section.details"
                        defaultMessage="Details & Serviceinfos"
                        description="The headline for the prouct details card section"
                      />
                    }
                  >
                    <ProductDetails
                      data-cypress-id="product-details"
                      twoColumn={
                        Object.keys(attributes).some((key) =>
                          config.productAttributes.includes(key),
                        ) || config.installmentCalculator.cardLayout
                      }
                      mobile={isMobile}
                    >
                      <div id="product-description-column">
                        <Card
                          collapsible
                          contentPadding
                          headline={<ProductHeadline product={product} />}
                          scrollTarget={
                            isMobile ? '#mobile-card-scroll-target' : '#thumbnail-slider'
                          }
                        >
                          <ProductDescription product={product} />
                        </Card>
                        {typeof window !== 'undefined' &&
                          config.externalServices.loadbee.enabled && <LoadBee ean={ean} />}
                      </div>
                      <div id="product-attributes-column">
                        <ProductAttributes attributes={attributes} />
                        {config.installmentCalculator.cardLayout && (
                          <>
                            <div id="card-installment-calculator" />
                            <InstallmentCalculatorVariant />
                          </>
                        )}
                      </div>
                    </ProductDetails>
                  </Section>
                  {typeof window !== 'undefined' && (
                    <div>
                      {product && config.companyId !== 'emma' ? (
                        <Section
                          headline={
                            <FormattedMessage
                              id="page.section.recommendations"
                              defaultMessage="Empfehlungen für dich"
                            />
                          }
                        >
                          <div>
                            {product.hasSeries && (
                              <ProductRecoSlider
                                recoApi={config.recommendationAPI}
                                pageTemplate="detailview"
                                headline={intl.formatMessage(fallbackRDEHeadlines.seriesFallback)}
                                rdeRequest={{
                                  articleNo: `${orderNumber}${promotion}`,
                                  rdeType: 'seriesProducts',
                                }}
                              />
                            )}
                            <ProductRecoSlider
                              recoApi={config.recommendationAPI}
                              pageTemplate="detailview"
                              headline={intl.formatMessage(fallbackRDEHeadlines.prudSysFallback)}
                              rdeRequest={{
                                articleNo: orderNumber,
                                cid: category !== null ? category : defaultCategory,
                                rdeType: 'prudSysProducts',
                              }}
                            />
                            <ScrollTarget id="customer-ratings">.</ScrollTarget>
                            <ProductRecoSlider
                              recoApi={config.recommendationAPI}
                              pageTemplate="detailview"
                              headline={intl.formatMessage(fallbackRDEHeadlines.criteoFallback)}
                              rdeRequest={{
                                articleNo: orderNumber,
                                available:
                                  product.availability &&
                                  product.availability.state === 'NOT_AVAILABLE'
                                    ? 0
                                    : 1,
                                brand: product.brand ? product.brand.name : '',
                                cid: category !== null ? category : defaultCategory,
                                price: parseFloat(product.price.value.toFixed(2)),
                                rdeType: 'criteoProducts',
                              }}
                            />
                          </div>
                        </Section>
                      ) : (
                        <ScrollTarget id="customer-ratings">.</ScrollTarget>
                      )}
                    </div>
                  )}
                  <CustomerRatings />
                  <div>
                    {typeof window !== 'undefined' && product && config.companyId !== 'emma' && (
                      <Section
                        headline={
                          <FormattedMessage
                            id="page.section.recommendations"
                            defaultMessage="Empfehlungen für dich"
                          />
                        }
                      >
                        <ProductRecoSlider
                          recoApi={config.recommendationAPI}
                          pageTemplate="detailview"
                          headline={intl.formatMessage(
                            fallbackRDEHeadlines.alternativeProductsFallback,
                          )}
                          rdeRequest={{
                            articleNo: `${orderNumber}${promotion}`,
                            cid: category !== null ? category : defaultCategory,
                            rdeType: 'similarProducts',
                          }}
                        />
                      </Section>
                    )}
                  </div>
                  {config.seo.enabled && !seoLoading && !seoError && seoData && (
                    <div>
                      <SeoImageLink />
                      <SeoElement data={seoData} />
                    </div>
                  )}
                </InstallmentCalculatorContextProvider>
              </CollapsibleContextProvider>
            </StickyContext.Provider>
          </WishlistProvider>
        </>
      </PageWrapper>
    </>
  );

  return page;
};

export default PageContainer;
